<script setup>
/**
 * O CARTÃO VIVO DA ASSINATURA, NA CONVERSA (Minutas, 29/09/2026).
 *
 * Nota da equipe (o cliente não vê). Nasce do retrato que o servidor grava em
 * `content_attributes.data.assinatura` e se atualiza por dois caminhos:
 *   - o `MESSAGE_UPDATED` do websocket (o servidor reescreve o retrato a cada
 *     notícia do assinador);
 *   - a leitura da API de 20 em 20 s enquanto o envio está aberto — websocket
 *     cai, e um cartão parado em "aguardando" com o cliente já tendo assinado
 *     é pior que nenhum.
 * Nunca mostra o link: "Copiar link" o busca na hora, por um endpoint que
 * registra quem pediu.
 */
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useConfirmacao } from 'dashboard/composables/useConfirmacao';
import AssinaturasAPI from 'dashboard/api/assinaturas';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  chaveDoStatus,
  diaEHora,
  estaAberto,
  etapasDoCartao,
  progresso,
  tomDoStatus,
} from './assinatura';

const props = defineProps({
  dados: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();

const ICONE_DA_ETAPA = {
  enviado: 'i-hugeicons-sent',
  abriu: 'i-hugeicons-view',
  cliente: 'i-hugeicons-signature',
  advogado: 'i-hugeicons-briefcase-01',
  concluido: 'i-hugeicons-checkmark-badge-01',
};

const fresco = ref(null);
const linhaDoTempo = ref([]);
const detalhes = ref(false);
const ocupado = ref('');
const copiado = ref(false);

// O retrato mais novo vence: o da mensagem (websocket) ou o da leitura.
const envelope = computed(() => {
  const daMensagem = props.dados || {};
  if (!fresco.value) return daMensagem;
  return fresco.value.envelopeId === daMensagem.envelopeId
    ? fresco.value
    : daMensagem;
});
watch(
  () => props.dados,
  () => {
    fresco.value = null;
  },
  { deep: true }
);

const etapas = computed(() => etapasDoCartao(envelope.value));
const barra = computed(() => ({
  '--ass-progresso': progresso(envelope.value),
}));
const tom = computed(() => tomDoStatus(envelope.value));
const status = computed(() => chaveDoStatus(envelope.value));
const aberto = computed(() => estaAberto(envelope.value));
const concluido = computed(() => envelope.value.status === 'concluido');
const parado = computed(
  () => envelope.value.status === 'rascunho' && Boolean(envelope.value.falha)
);
const esperandoResposta = computed(
  () =>
    envelope.value.status === 'aguardando' &&
    envelope.value.entrega === 'modelo' &&
    !envelope.value.linkEnviadoEm
);
const temLink = computed(() =>
  (envelope.value.assinantes || []).some(
    a => a.papel === 'cliente' && a.temLink
  )
);
const hora = diaEHora;
// Prazo perto (menos de 48 h) pede você: o selo do prazo fica âmbar.
const prazoPerto = computed(() => {
  const prazo = envelope.value.prazo && new Date(envelope.value.prazo);
  return Boolean(prazo) && prazo - Date.now() < 48 * 3600 * 1000;
});
const conta = computed(() => store.getters.getCurrentAccountId);

// ── A vez do escritório (30/09/2026) ───────────────────────────────────────
// Victor: "não ficou claro como o advogado vai assinar". Depois que o cliente
// assina, o cartão diz QUEM do escritório falta e, para a própria advogada
// logada, oferece "Assinar agora" (o link dela só sai para ela).
const assinantes = computed(() => envelope.value.assinantes || []);
const clienteAssinou = computed(() =>
  assinantes.value.some(a => a.papel === 'cliente' && a.status === 'assinou')
);
const advogadosPendentes = computed(() =>
  assinantes.value.filter(a => a.papel === 'advogado' && a.status !== 'assinou')
);
// "JULLYA OLIVEIRA BATISTA DE ANDRADE" → "Jullya Andrade": curto e legível.
const nomeCurto = nome => {
  const partes = String(nome || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map(p => p.charAt(0).toUpperCase() + p.slice(1));
  return partes.length > 1 ? `${partes[0]} ${partes.at(-1)}` : partes[0] || '';
};
const faltaDoEscritorio = computed(() =>
  advogadosPendentes.value.map(a => nomeCurto(a.nome)).join(', ')
);
const minhaVez = computed(() =>
  advogadosPendentes.value.some(
    a => a.userId === store.getters.getCurrentUser?.id && a.temLink
  )
);
// A aba abre JÁ no clique (depois de esperar o servidor, o navegador a
// bloquearia como pop-up) e recebe o link quando ele chega.
const assinarAgora = async () => {
  const aba = window.open('', '_blank');
  ocupado.value = 'assinar';
  try {
    const { data } = await AssinaturasAPI.meuLink(envelope.value.envelopeId);
    if (aba) {
      aba.opener = null;
      aba.location.href = data.payload.link;
    } else {
      window.open(data.payload.link, '_blank', 'noopener');
    }
  } catch (e) {
    aba?.close();
    useAlert(
      e?.response?.data?.errors?.[0] || t('ASSINATURAS.CARTAO.FALHA_ACAO')
    );
  } finally {
    ocupado.value = '';
  }
};

// ── Leitura ─────────────────────────────────────────────────────────────────
const ler = async () => {
  if (!envelope.value.envelopeId) return;
  try {
    const { data } = await AssinaturasAPI.ler(envelope.value.envelopeId);
    const lido = useCamelCase(data.payload, { deep: true });
    linhaDoTempo.value = lido.linhaDoTempo || [];
    fresco.value = lido;
  } catch {
    // A próxima volta tenta de novo; o cartão fica com o último retrato.
  }
};

let relogio = null;
const vigiar = () => {
  clearInterval(relogio);
  relogio = aberto.value || parado.value ? setInterval(ler, 20000) : null;
};
watch(aberto, vigiar, { immediate: true });
onUnmounted(() => clearInterval(relogio));

// ── Ações ───────────────────────────────────────────────────────────────────
// A mensagem nova (bolha do link) entra na lista na hora, sem esperar o
// websocket. Falhar aqui não é falha da ação: a lista se acerta na próxima
// leitura da conversa.
const mostrarNaConversa = mensagens =>
  (mensagens || []).forEach(m =>
    store.dispatch('addMessage', m).catch(() => {})
  );

const executar = async nome => {
  const { data } = await AssinaturasAPI.acao(envelope.value.envelopeId, nome);
  fresco.value = useCamelCase(data.payload, { deep: true });
  linhaDoTempo.value = fresco.value.linhaDoTempo || linhaDoTempo.value;
  mostrarNaConversa(data.mensagens);
};

const agir = async (nome, sucesso) => {
  ocupado.value = nome;
  try {
    await executar(nome);
    if (sucesso) useAlert(sucesso);
  } catch (e) {
    useAlert(
      e?.response?.data?.errors?.[0] || t('ASSINATURAS.CARTAO.FALHA_ACAO')
    );
  } finally {
    ocupado.value = '';
  }
};

// Cancelar não tem volta (o link deixa de valer): pede confirmação no
// diálogo do produto, nunca no `confirm()` do navegador.
const { dialogo, textos, pedirConfirmacao, confirmar, confirmando } =
  useConfirmacao(t('ASSINATURAS.CARTAO.FALHA_ACAO'));
const pedirCancelamento = () =>
  pedirConfirmacao(
    async () => {
      await executar('cancelar');
      useAlert(t('ASSINATURAS.CARTAO.CANCELADO_OK'));
    },
    {
      titulo: t('ASSINATURAS.CARTAO.CANCELAR_TITULO'),
      descricao: t('ASSINATURAS.CARTAO.CANCELAR_CONFIRMA'),
      rotulo: t('ASSINATURAS.CARTAO.CANCELAR_SIM'),
    }
  );

const copiarLink = async () => {
  ocupado.value = 'copiar';
  try {
    const { data } = await AssinaturasAPI.link(envelope.value.envelopeId);
    await navigator.clipboard.writeText(data.payload.link);
    copiado.value = true;
    setTimeout(() => {
      copiado.value = false;
    }, 1800);
  } catch (e) {
    useAlert(
      e?.response?.data?.errors?.[0] || t('ASSINATURAS.CARTAO.FALHA_ACAO')
    );
  } finally {
    ocupado.value = '';
  }
};

const alternarDetalhes = () => {
  detalhes.value = !detalhes.value;
  if (detalhes.value) ler();
};

const textoDoEvento = ev =>
  t(`ASSINATURAS.CARTAO.EVENTO.${ev.tipo}`, { quem: ev.quem || '' });
</script>

<template>
  <article
    class="advup-card ass-cartao"
    :class="[`ass-cartao--${tom}`, { 'ass-cartao--feito': concluido }]"
    :aria-label="`${t('ASSINATURAS.CARTAO.ROTULO')}: ${envelope.nome}`"
  >
    <span class="ass-cartao__fita" aria-hidden="true" />
    <header class="ass-cartao__cab">
      <span class="ass-cartao__ladrilho" aria-hidden="true">
        <i
          :class="concluido ? 'i-ph-seal-check-fill' : 'i-ph-signature-fill'"
        />
      </span>
      <div class="ass-cartao__id">
        <p class="advup-rotulo">{{ t('ASSINATURAS.CARTAO.ROTULO') }}</p>
        <p class="ass-cartao__nome">{{ envelope.nome }}</p>
      </div>
      <span class="ficha-pill" :class="`ficha-pill--${tom}`">
        {{ t(`ASSINATURAS.CARTAO.STATUS.${status}`) }}
      </span>
    </header>

    <!-- A trilha: cinco marcos e a barra que cresce entre eles. -->
    <ol class="ass-trilha" :style="barra">
      <li
        v-for="etapa in etapas"
        :key="etapa.chave"
        class="ass-trilha__marco"
        :class="{
          'ass-trilha__marco--feito': etapa.feita,
          'ass-trilha__marco--agora': etapa.atual,
        }"
      >
        <span class="ass-trilha__ico" aria-hidden="true">
          <i
            :class="
              etapa.feita ? 'i-hugeicons-tick-02' : ICONE_DA_ETAPA[etapa.chave]
            "
          />
        </span>
        <span class="ass-trilha__rot">
          {{ t(`ASSINATURAS.CARTAO.ETAPA.${etapa.chave}`) }}
        </span>
        <span v-if="etapa.feita && etapa.quando" class="ass-trilha__hora">
          {{ hora(etapa.quando) }}
        </span>
      </li>
    </ol>

    <p
      v-if="esperandoResposta"
      class="ass-cartao__nota ass-cartao__nota--ambar"
    >
      <i class="i-hugeicons-clock-05" aria-hidden="true" />
      {{ t('ASSINATURAS.CARTAO.AGUARDANDO_RESPOSTA') }}
    </p>
    <p
      v-if="
        envelope.falha &&
        !['concluido', 'cancelado', 'recusado'].includes(envelope.status)
      "
      class="ass-cartao__nota ass-cartao__nota--rubi"
      role="alert"
    >
      <i class="i-hugeicons-alert-circle" aria-hidden="true" />
      <span>
        <strong v-if="parado">{{ t('ASSINATURAS.CARTAO.PARADO') }} — </strong>
        {{ envelope.falha }}
      </span>
    </p>
    <p
      v-if="envelope.prazo && aberto"
      class="ass-cartao__prazo"
      :class="{ 'ass-cartao__prazo--perto': prazoPerto }"
    >
      <i class="i-hugeicons-calendar-03" aria-hidden="true" />
      {{ t('ASSINATURAS.CARTAO.PRAZO', { data: hora(envelope.prazo) }) }}
    </p>

    <p
      v-if="
        envelope.status === 'aguardando' &&
        clienteAssinou &&
        advogadosPendentes.length
      "
      class="ass-cartao__nota ass-cartao__nota--azul"
    >
      <i class="i-hugeicons-briefcase-01" aria-hidden="true" />
      <span>
        <strong>{{ t('ASSINATURAS.CARTAO.VEZ_DO_ESCRITORIO') }}</strong>
        {{
          t('ASSINATURAS.CARTAO.FALTA_ESCRITORIO', {
            nomes: faltaDoEscritorio,
          })
        }}
      </span>
    </p>

    <div class="ass-cartao__acoes">
      <button
        v-if="envelope.status === 'aguardando' && minhaVez"
        type="button"
        class="advup-btn-forte ass-cartao__btn"
        :disabled="Boolean(ocupado)"
        @click="assinarAgora"
      >
        <i
          :class="
            ocupado === 'assinar'
              ? 'i-lucide-loader-2 advup-gira'
              : 'i-hugeicons-signature'
          "
          aria-hidden="true"
        />
        {{ t('ASSINATURAS.CARTAO.ASSINAR_AGORA') }}
      </button>
      <button
        v-if="parado"
        type="button"
        class="advup-btn-forte ass-cartao__btn"
        :disabled="Boolean(ocupado)"
        @click="agir('tentar_de_novo')"
      >
        <i class="i-hugeicons-reload" aria-hidden="true" />
        {{ t('ASSINATURAS.GAVETA.TENTAR_DE_NOVO') }}
      </button>
      <!-- O que é do cliente some quando ele já assinou. -->
      <template v-if="envelope.status === 'aguardando' && !clienteAssinou">
        <button
          type="button"
          class="advup-btn ass-cartao__btn"
          :disabled="!temLink || Boolean(ocupado)"
          @click="copiarLink"
        >
          <i
            :class="copiado ? 'i-hugeicons-tick-02' : 'i-hugeicons-copy-01'"
            aria-hidden="true"
          />
          {{
            copiado
              ? t('ASSINATURAS.CARTAO.COPIADO')
              : t('ASSINATURAS.CARTAO.COPIAR')
          }}
        </button>
        <button
          type="button"
          class="advup-btn ass-cartao__btn"
          :disabled="Boolean(ocupado)"
          @click="agir('reenviar', t('ASSINATURAS.CARTAO.ENVIADO_OK'))"
        >
          <i class="i-hugeicons-sent" aria-hidden="true" />
          {{ t('ASSINATURAS.CARTAO.REENVIAR') }}
        </button>
        <button
          type="button"
          class="advup-btn ass-cartao__btn"
          :disabled="Boolean(ocupado) || !envelope.linkEnviadoEm"
          @click="agir('lembrar', t('ASSINATURAS.CARTAO.LEMBRETE_OK'))"
        >
          <i class="i-hugeicons-notification-01" aria-hidden="true" />
          {{ t('ASSINATURAS.CARTAO.LEMBRAR') }}
        </button>
      </template>
      <router-link
        v-if="concluido && envelope.pastaId"
        class="advup-btn ass-cartao__btn"
        :to="`/app/accounts/${conta}/processos/${envelope.pastaId}`"
      >
        <i class="i-hugeicons-folder-open" aria-hidden="true" />
        {{ t('ASSINATURAS.CARTAO.PASTA') }}
      </router-link>
      <span class="ass-cartao__espaco" />
      <button
        v-if="aberto || parado"
        v-tooltip.top="t('ASSINATURAS.CARTAO.ATUALIZAR')"
        type="button"
        class="advup-acao advup-acao--p"
        :aria-label="t('ASSINATURAS.CARTAO.ATUALIZAR')"
        :disabled="Boolean(ocupado)"
        @click="agir('sincronizar')"
      >
        <i class="i-hugeicons-refresh" aria-hidden="true" />
      </button>
      <button
        v-if="envelope.status === 'aguardando' || parado"
        v-tooltip.top="t('ASSINATURAS.CARTAO.CANCELAR')"
        type="button"
        class="advup-acao advup-acao--p advup-acao--perigo"
        :aria-label="t('ASSINATURAS.CARTAO.CANCELAR')"
        :disabled="Boolean(ocupado)"
        @click="pedirCancelamento"
      >
        <i class="i-hugeicons-cancel-circle" aria-hidden="true" />
      </button>
      <button
        type="button"
        class="advup-acao advup-acao--p"
        :aria-label="
          detalhes
            ? t('ASSINATURAS.CARTAO.OCULTAR')
            : t('ASSINATURAS.CARTAO.DETALHES')
        "
        :aria-expanded="detalhes"
        @click="alternarDetalhes"
      >
        <i
          class="i-hugeicons-arrow-down-01 ass-cartao__seta"
          :class="{ 'ass-cartao__seta--aberta': detalhes }"
          aria-hidden="true"
        />
      </button>
    </div>

    <div
      class="ass-cartao__gaveta"
      :class="{ 'ass-cartao__gaveta--aberta': detalhes }"
    >
      <div class="ass-cartao__gaveta-dentro">
        <p class="advup-rotulo">{{ t('ASSINATURAS.CARTAO.LINHA_DO_TEMPO') }}</p>
        <ol class="ass-tempo">
          <li
            v-for="(ev, i) in linhaDoTempo"
            :key="`${ev.tipo}-${ev.quando}-${i}`"
            class="ass-tempo__item"
          >
            <span class="ass-tempo__txt">{{ textoDoEvento(ev) }}</span>
            <span class="ass-tempo__hora">{{ hora(ev.quando) }}</span>
          </li>
        </ol>
      </div>
    </div>

    <Dialog
      ref="dialogo"
      type="alert"
      :title="textos.titulo"
      :description="textos.descricao"
      :confirm-button-label="textos.rotulo"
      :cancel-button-label="t('ASSINATURAS.CARTAO.CANCELAR_NAO')"
      :is-loading="confirmando"
      @confirm="confirmar"
    />

    <p class="ass-cartao__rodape">
      <i class="i-hugeicons-view-off-slash" aria-hidden="true" />
      {{ t('ASSINATURAS.CARTAO.SO_EQUIPE') }}
    </p>
  </article>
</template>

<style scoped>
@property --ass-progresso {
  syntax: '<number>';
  inherits: true;
  initial-value: 0;
}

.ass-cartao {
  --tom: var(--blue-9);
  --tom-tinta: var(--blue-11);

  position: relative;
  width: min(36rem, 100%);
  margin: var(--esp-2) auto;
  padding: var(--esp-4) var(--esp-5);
  overflow: hidden;
  border: 1px solid rgb(var(--slate-5));
  border-radius: var(--r-4);
  /* Redesenho 30/09/2026 ("mais vida, elegância"): o véu era 8% num canto e
     o cartão lia chapado. Duas camadas no tom do estado — a forte escorre do
     canto do ladrilho, a leve fecha o outro lado — e morrem antes da metade. */
  background: radial-gradient(
      110% 95% at 0% 0%,
      rgb(var(--tom) / 0.13),
      transparent 52%
    ),
    radial-gradient(80% 70% at 100% 0%, rgb(var(--tom) / 0.06), transparent 60%),
    rgb(var(--solid-1));
  box-shadow: 0 1px 2px rgb(15 23 42 / 0.06);
  animation: ass-cartao-chega var(--mov-3) var(--mov-curva);
  transition:
    box-shadow var(--mov-2) var(--mov-curva),
    border-color var(--mov-2) var(--mov-curva);
}

/* A sombra ambiente é resposta ao mouse, não textura (regra 2). */
.ass-cartao:hover {
  border-color: rgb(var(--tom) / 0.35);
  box-shadow:
    0 1px 2px rgb(15 23 42 / 0.06),
    0 14px 32px -18px rgb(var(--tom) / 0.45);
}

/* A fita do estado, de ponta a ponta no topo. */
.ass-cartao__fita {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 3px;
  background: linear-gradient(
    90deg,
    rgb(var(--tom)),
    color-mix(in srgb, rgb(var(--tom)) 55%, transparent)
  );
}

@keyframes ass-cartao-chega {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
}

.ass-cartao--ok {
  --tom: var(--teal-9);
  --tom-tinta: var(--teal-11);
}

.ass-cartao--alerta {
  --tom: var(--ruby-9);
  --tom-tinta: var(--ruby-11);
}

.ass-cartao--ambar {
  --tom: var(--amber-9);
  --tom-tinta: var(--amber-11);
}

.ass-cartao--neutro {
  --tom: var(--slate-9);
  --tom-tinta: var(--slate-11);
}

.ass-cartao__cab {
  display: flex;
  align-items: center;
  gap: var(--esp-3);
}

.ass-cartao__ladrilho {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: var(--r-3);
  background: linear-gradient(
    150deg,
    rgb(var(--tom)) 0%,
    color-mix(in srgb, rgb(var(--tom)) 78%, #000) 100%
  );
  box-shadow:
    0 1px 2px rgb(var(--tom) / 0.45),
    0 8px 18px -8px rgb(var(--tom) / 0.8);
  color: #fff;
}

.ass-cartao__ladrilho > i {
  width: var(--ico-3);
  height: var(--ico-3);
}

.ass-cartao--feito .ass-cartao__ladrilho > i {
  animation: ass-selo var(--mov-3) var(--mov-curva-pulo);
}

@keyframes ass-selo {
  from {
    transform: scale(0.4) rotate(-20deg);
    opacity: 0;
  }
}

.ass-cartao__id {
  flex: 1;
  min-width: 0;
}

.ass-cartao__nome {
  margin: 0;
  overflow: hidden;
  font-size: var(--txt-bloco);
  font-weight: var(--peso-forte);
  line-height: var(--linha-normal);
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgb(var(--slate-12));
}

.ass-trilha {
  position: relative;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  margin: var(--esp-4) 0 var(--esp-2);
  padding: 0;
  list-style: none;
}

/* O trilho e a barra que cresce: a barra é o progresso real (0 a 1),
   animado por `@property` — sem ela, a largura trocaria de uma vez. */
.ass-trilha::before,
.ass-trilha::after {
  position: absolute;
  top: 13px;
  left: 10%;
  height: 2px;
  border-radius: var(--r-redondo);
  content: '';
}

.ass-trilha::before {
  right: 10%;
  background: rgb(var(--slate-5));
}

.ass-trilha::after {
  width: calc(80% * var(--ass-progresso));
  background: linear-gradient(90deg, rgb(var(--tom-tinta)), rgb(var(--tom)));
  box-shadow: 0 0 8px rgb(var(--tom) / 0.35);
  transition: --ass-progresso var(--mov-4) var(--mov-curva);
}

.ass-trilha__marco {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--esp-1);
  text-align: center;
}

.ass-trilha__ico {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: 2px solid rgb(var(--slate-5));
  border-radius: var(--r-redondo);
  background: rgb(var(--solid-1));
  color: rgb(var(--slate-10));
  transition:
    background-color var(--mov-2) var(--mov-curva),
    border-color var(--mov-2) var(--mov-curva),
    color var(--mov-2) var(--mov-curva);
}

.ass-trilha__ico > i {
  width: var(--ico-1);
  height: var(--ico-1);
}

.ass-trilha__marco--feito .ass-trilha__ico {
  border-color: transparent;
  background: linear-gradient(
    150deg,
    rgb(var(--tom)),
    color-mix(in srgb, rgb(var(--tom)) 80%, #000)
  );
  box-shadow: 0 4px 10px -4px rgb(var(--tom) / 0.7);
  color: #fff;
}

/* O marco de agora: pulsa três vezes ao chegar e fica com o halo parado —
   diz "é aqui" sem piscar sozinho (regra 4). */
.ass-trilha__marco--agora .ass-trilha__ico {
  border-color: rgb(var(--tom));
  box-shadow: 0 0 0 4px rgb(var(--tom) / 0.14);
  color: rgb(var(--tom-tinta));
  animation: ass-pulso var(--mov-pulso) var(--mov-curva) 3;
}

@keyframes ass-pulso {
  50% {
    box-shadow: 0 0 0 5px rgb(var(--tom) / 0.18);
  }
}

.ass-trilha__rot {
  font-size: var(--txt-nota);
  line-height: var(--linha-apertada);
  color: rgb(var(--slate-11));
}

.ass-trilha__marco--feito .ass-trilha__rot,
.ass-trilha__marco--agora .ass-trilha__rot {
  font-weight: var(--peso-dado);
  color: rgb(var(--slate-12));
}

.ass-trilha__marco--agora .ass-trilha__rot {
  color: rgb(var(--tom-tinta));
}

.ass-trilha__hora {
  font-size: var(--txt-nota);
  line-height: var(--linha-apertada);
  font-variant-numeric: tabular-nums;
  color: rgb(var(--slate-11));
}

.ass-cartao__nota {
  display: flex;
  gap: var(--esp-2);
  margin: var(--esp-2) 0 0;
  padding: var(--esp-2) var(--esp-3);
  border-radius: var(--r-3);
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
}

.ass-cartao__nota > i {
  width: var(--ico-2);
  height: var(--ico-2);
  flex-shrink: 0;
}

.ass-cartao__nota--ambar {
  background: rgb(var(--amber-3));
  color: rgb(var(--amber-12));
}

.ass-cartao__nota--rubi {
  background: rgb(var(--ruby-3));
  color: rgb(var(--ruby-12));
}

.ass-cartao__nota--azul {
  background: rgb(var(--blue-3));
  color: rgb(var(--blue-12));
}

.ass-cartao__prazo {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  margin: var(--esp-2) 0 0;
  padding: var(--esp-1) var(--esp-2);
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-3));
  font-size: var(--txt-nota);
  line-height: var(--linha-apertada);
  font-variant-numeric: tabular-nums;
  color: rgb(var(--slate-11));
}

.ass-cartao__prazo > i {
  width: var(--ico-1);
  height: var(--ico-1);
}

.ass-cartao__prazo--perto {
  background: rgb(var(--amber-3));
  color: rgb(var(--amber-12));
}

.ass-cartao__acoes {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--esp-2);
  margin-top: var(--esp-3);
}

.ass-cartao__btn {
  gap: var(--esp-1);
}

.ass-cartao__btn > i {
  width: var(--ico-2);
  height: var(--ico-2);
}

.ass-cartao__espaco {
  flex: 1;
}

.ass-cartao__seta {
  transition: transform var(--mov-2) var(--mov-curva);
}

.ass-cartao__seta--aberta {
  transform: rotate(180deg);
}

/* Abre sem saber a altura: `grid-template-rows` 0fr → 1fr. */
.ass-cartao__gaveta {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows var(--mov-3) var(--mov-curva);
}

.ass-cartao__gaveta--aberta {
  grid-template-rows: 1fr;
}

.ass-cartao__gaveta-dentro {
  min-height: 0;
  overflow: hidden;
}

.ass-cartao__gaveta--aberta .ass-cartao__gaveta-dentro {
  padding-top: var(--esp-3);
}

.ass-tempo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  margin: var(--esp-2) 0 0;
  padding: 0;
  list-style: none;
}

.ass-tempo__item {
  display: flex;
  justify-content: space-between;
  gap: var(--esp-3);
  font-size: var(--txt-nota);
  line-height: var(--linha-normal);
}

.ass-tempo__txt {
  color: rgb(var(--slate-12));
}

.ass-tempo__hora {
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
  color: rgb(var(--slate-11));
}

.ass-cartao__rodape {
  display: flex;
  align-items: center;
  gap: var(--esp-1);
  margin: var(--esp-3) 0 0;
  padding-top: var(--esp-3);
  border-top: 1px dashed rgb(var(--slate-5));
  font-size: var(--txt-nota);
  line-height: var(--linha-apertada);
  color: rgb(var(--slate-11));
}

.ass-cartao__rodape > i {
  width: var(--ico-1);
  height: var(--ico-1);
}

@media (prefers-reduced-motion: reduce) {
  .ass-trilha__marco--agora .ass-trilha__ico {
    animation: none;
  }
}

@media (max-width: 639px) {
  .ass-cartao {
    padding: var(--esp-4);
  }

  .ass-trilha__rot {
    font-size: var(--txt-rotulo);
  }

  .ass-trilha__hora {
    display: none;
  }

  .ass-cartao__btn,
  .ass-cartao__acoes .advup-acao {
    min-height: 44px;
  }
}
</style>
